import ApiService from '@/common/api.service'

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param contact Object
 *
 * @return Object
 */
function transform (contact) {
  return {
    id: contact.id,
    contact_type_id: contact.contact_type_id,
    contact_name: contact.contact_name,
    cellphone: contact.cellphone,
    phone_office: contact.phone_office,
    phone_home: contact.phone_home,
    email: contact.email,
    fax: contact.fax,
    note: contact.note
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
export function defaults () {
  return {
    isEditing: false,
    isAdding: false,
    isDeleting: false,
    warning: '',
    contacts: [],
    contact_types: {}
  }
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns an array of contacts
   *
   * @return Array
   */
  contacts (state) {
    return state.contacts
  },

  /**
   * Returns the list of contact types.
   *
   * @param state Object
   *
   * @return Array
   */
  contactTypes (state) {
    return state.contact_types
  },

  /**
   * Returns the current form warning.
   *
   * @param state Object
   *
   * @return string
   */
  warning (state) {
    return state.warning
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   * @param level   String Example: 'partners', 'agencies', 'clients'
   *
   * @return Promise
   */
  fetch (context, { id, level }) {
    return ApiService
      .get(`${level}/${id}/contacts`)
      .then((response) => {
        context.commit('setContacts', response.data.data.contacts)
        return response
      })
  },

  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param level   String Example: 'partners', 'agencies', 'clients'
   *
   * @return Promise
   */
  fetchContactTypes (context, level) {
    return ApiService
      .get(`${level}/contact-types`)
      .then((response) => {
        context.commit('setContactTypes', response.data.data.contact_types)
        return response
      })
  },

  /**
   * API request to create a new contact. The payload must be in the format:
   * { url: String, data: Object }
   *
   * @return Promise
   */
  post (context, payload) {
    return ApiService
      .post(payload.url, transform(payload.data))
      .then((response) => {
        context.commit('add', response.data.data)
        return response
      })
  },

  /**
   * API request to update an existing contact. The payload must be in the format:
   * { url: String, data: Object }
   *
   * @return Promise
   */
  put (context, payload) {
    return ApiService
      .put(payload.url, transform(payload.data))
      .then((response) => {
        context.commit('update', response.data.data)
        return response
      })
  },

  /**
   * API request to delete an existing contact. The payload must be in the format:
   * { url: String, data: Object  }
   *
   * @return Promise
   */
  delete (context, payload) {
    return ApiService
      .delete(payload.url)
      .then((response) => {
        context.commit('removeById', payload.data.id)
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`clients/contacts`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set an array of contacts.
   *
   * @param state    Object
   * @param contacts Array
   */
  setContacts (state, contacts) {
    state.contacts = contacts
  },

  /**
   * Set the contact types.
   *
   * @param state        Object
   * @param contactTypes Object
   */
  setContactTypes (state, contactTypes) {
    state.contact_types = contactTypes
  },

  /**
   * Add a new contact.
   *
   * @param state   Object
   * @param contact Object
   */
  add (state, contact) {
    state.contacts.push(contact)
  },

  /**
   * Updates an existing contact.
   *
   * @param state   Object
   * @param contact Object
   */
  update (state, contact) {
    let index = state.contacts.findIndex(element => element.id === contact.id)
    state.contacts[index] = contact
  },

  /**
   * Find a contact by index in the array of contacts and updates it.
   *
   * @param state   Object
   * @param contact Object
   * @param index   Number
   */
  updateByIndex (state, {contact, index}) {
    state.contacts[index] = contact
  },

  /**
   * Updates the last contact that was added.
   *
   * @param state   Object
   * @param contact Object
   */
  updateNewest (state, contact) {
    state.contacts[state.contacts.length - 1] = contact
  },

  /**
   * Find a contact by id and remove it.
   *
   * @param state Object
   * @param id    Number
   */
  removeById (state, id) {
    let index = state.contacts.findIndex(element => element.id === id)
    state.contacts.splice(index, 1)
  },

  /**
   * Remove a contact by index in the array of contacts.
   *
   * @param state Object
   * @param index Number
   */
  removeByIndex (state, index) {
    state.contacts.splice(index, 1)
  },

  /**
   * Clears both the contacts and contact types from the state.
   *
   * @param state Object
   */
  purge (state) {
    state.contacts = []
    state.contact_types = {}
  },

  /**
   * Purge the contact types only and not the contact types.
   *
   * @param state Object
   */
  purgeContacts (state) {
    state.contacts = []
  },

  /**
   * Sets the isAdding form state.
   *
   * @param state   Object
   * @param isAdding Boolean
   */
  setIsAdding (state, isAdding) {
    state.isAdding = isAdding
  },

  /**
   * Sets the isEditing form state.
   *
   * @param state     Object
   * @param isEditing Boolean
   */
  setIsEditing (state, isEditing) {
    state.isEditing = isEditing
  },

  /**
   * Sets the isDeleting form state.
   *
   * @param state      Object
   * @param isDeleting Boolean
   */
  setIsDeleting (state, isDeleting) {
    state.isDeleting = isDeleting
  },

  /**
   * Sets the form warning.
   *
   * @param state      Object
   * @param warning Boolean
   */
  setWarning (state, warning) {
    state.warning = warning
  },

  /**
   * Resets the form's state.
   *
   * @param state      Object
   */
  resetFormState (state) {
    state.isAdding = false
    state.isEditing = false
    state.isDeleting = false
    state.warning = ''
  }
}

export default {
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/contacts/contactsModule.js